import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { brandsService } from "api/services/brands.service";
import { usersService } from "api/services/users.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { getApiErrorMessage } from "utils/api-error";
import { decideMemberDeepLink, isCompleteKoreanPhoneNumber, isLegacyPk } from "utils/member-search";

import UserContactEditModal from "./UserContactEditModal";

import type { Brand } from "api/services/brands.service";
import type {
  UserDetail as UserDetailType,
  UserLevel,
  InstructorPublicDetail,
} from "api/types/user.types";
import "pages/AdminPage.scss";
import "./UserDetail.scss";

type RoleType = "user" | "instructor" | "admin";

const ROLE_OPTIONS: { value: RoleType; label: string }[] = [
  { value: "user", label: "수강생" },
  { value: "instructor", label: "강사" },
  { value: "admin", label: "관리자" },
];

const getUserRole = (user: UserDetailType): RoleType => {
  if (user.is_admin) return "admin";
  if (user.is_staff) return "instructor";
  return "user";
};

const roleToFlags = (role: RoleType) => ({
  is_admin: role === "admin",
  is_staff: role === "instructor" || role === "admin",
});

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // 상세를 못 불러온 이유가 "없음" 이 아닌 경우(네트워크·5xx). `notFound` 와 구분한다.
  const [loadError, setLoadError] = useState("");
  const [levels, setLevels] = useState<UserLevel[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [instructor, setInstructor] = useState<InstructorPublicDetail | null>(null);

  // 현재 화면이 보고 있는 회원의 **세대**. mutation 응답이 도착했을 때 그 사이 화면이
  // 옮겨갔는지 판단한다 — 상세 조회만 막으면 강제 탈퇴·역할·브랜드·연락처 응답이
  // 여전히 이전 회원의 데이터로 화면을 덮는다.
  //
  // 라우트 `id` **값**을 비교하면 A→B→A 로 되돌아왔을 때 낡은 세대의 응답이 다시 가드를
  // 통과한다 (`stillViewing("A")` 가 또 true 다). 실제로 A 의 실패한 PATCH 가 복귀 후 성공한
  // 변경을 낡은 값으로 되돌리고, 성공한 변경에 "실패했습니다" alert 을 띄우며, 진행 중인
  // 요청의 잠금까지 풀어 이중 제출 창을 열었다. 그래서 **단조 증가 카운터**로 센다 —
  // 한 번 떠난 세대는 같은 회원으로 돌아와도 다시 유효해지지 않는다.
  //
  // 카운터는 **렌더 본문이 아니라 effect 에서** 올린다. 렌더 본문에서 ref 를 건드리면
  // concurrent 렌더가 버려졌을 때도 세대가 올라가, 화면은 그대로 A 인데 A 의 응답이
  // 낡은 것으로 버려지고 `saving` 잠금이 풀리지 않는다. effect 는 **커밋된 렌더**에서만
  // 도는 것이 보장되므로 "화면이 실제로 바뀌었다" 와 세대가 정확히 일치한다.
  const generationRef = useRef(0);
  /** 이 응답이 아직 유효한가 (요청 시점의 세대가 지금도 화면의 세대인가). */
  const stillViewing = (requestedGeneration: number) =>
    generationRef.current === requestedGeneration;

  useEffect(() => {
    // 커밋된 `id` 변경마다 세대를 하나 올린다. 이 지점이 유일한 증가점이다.
    generationRef.current += 1;
    if (!id) return;

    // `?id=` 딥링크는 판정으로 막았지만 **path segment** 로 들어오면 여기까지 온다 —
    // `/user-list/010-1234-5678`. 그대로 두면 번호가 요청 경로로 나가고 주소창·히스토리에
    // 남는다. 같은 판정을 태워 전화번호형이면 요청 없이 목록으로 정리한다.
    // 정규 10진수 PK 는 **전화 판정보다 앞선다** — BE 상세가 정수 PK 를 받는데 `158`·`1588`
    // 같은 값이 전화 접두로 걸려 요청조차 못 갔다. 범위·형식이 좁아 `01012345678` 같은
    // 번호를 PK 로 오인하지 않는다 (선행 0·부호·32-bit 초과는 PK 아님).
    // PK 는 허용하되 **완성형 전화번호는 제외**한다 — `15881234`·`16001234` 는 32-bit
    // 범위 안이라 PK 형식은 만족하지만 실제로는 전국대표번호다.
    // 다른 회원으로 이동하면 이전 회원의 진행 중 잠금은 이 화면과 무관하다 — 남겨두면
    // 새 회원 화면이 아무 이유 없이 잠긴 채로 열린다.
    setSaving(false);

    const rawTrimmed = (id ?? "").trim();
    const pathDecision =
      isLegacyPk(rawTrimmed) && !isCompleteKoreanPhoneNumber(rawTrimmed)
        ? { kind: "lookup" as const }
        : decideMemberDeepLink(id);
    // `too-long` 도 같은 처리다 — 상한을 넘는 경로는 어떤 식별자도 될 수 없고,
    // 그대로 두면 전화번호를 품은 106자 경로가 그대로 상세 요청이 되어 나간다.
    if (
      pathDecision.kind === "phone-blocked" ||
      pathDecision.kind === "invalid-empty" ||
      pathDecision.kind === "too-long"
    ) {
      navigate("/user-list", { replace: true });
      return;
    }

    setLoading(true);
    // 이전 `id` 의 실패 상태를 물려받지 않는다 — 404·500 을 본 뒤 정상 회원으로 이동하면
    // 상세가 200 으로 와도 실패 화면이 그대로 남는다.
    setNotFound(false);
    setLoadError("");
    // 연락처 수정 모달이 열린 채 다른 회원으로 이동하면 **이전 회원의 모달**이 남는다.
    setContactModalOpen(false);
    // 이전 회원의 강사 정보가 새 회원 화면에 잠시 남지 않게 즉시 비운다.
    setInstructor(null);
    // 언마운트·`id` 변경 후 도착한 응답이 화면을 건드리지 않게 한다.
    let cancelled = false;
    // 최초 상세 요청이 성공했는가. 이 뒤의 예외(levels·brands 후속 처리)는 **회원 없음이
    // 아니다** — 상세는 200 으로 왔고 화면에 이미 회원이 떠 있다.
    let detailLoaded = false;
    let userData: UserDetailType | null = null;
    usersService
      .getById(id)
      .then((res) => {
        // 성공 경로에도 가드가 필요하다. 없으면 A 상세를 여는 도중 B 로 이동했을 때
        // 뒤늦게 도착한 A 의 200 이 `setUser` 를 실행해 **주소는 B 인데 화면은 A** 가 된다.
        // 이후 mutation 대상까지 A 가 되는 상태 모순이라, 상태를 만지는 모든 지점 앞에 둔다.
        if (cancelled) return null;
        detailLoaded = true;
        userData = res.data;
        setUser(res.data);
        // 소속 브랜드 select 의 소스는 **하나**여야 한다. 강사면 강사 프로필(`instructor.brand`)
        // 이 유일한 소스이므로 여기서 건드리지 않는다 — 두 곳에서 쓰면 도착 순서에 따라
        // 값이 달라진다. 비강사만 home brand 로 초기화한다.
        setSelectedBrandId(res.data.is_staff ? null : (res.data.home_brand_id ?? null));
        return usersService.getLevels().catch(() => null);
      })
      .then((levelsRes) => {
        if (cancelled) return null;
        if (levelsRes && Array.isArray(levelsRes.data)) {
          setLevels(levelsRes.data);
        }
        return brandsService.list().catch(() => null);
      })
      .then((brandsRes) => {
        if (cancelled) return;
        if (brandsRes && Array.isArray(brandsRes.data)) {
          setBrands(brandsRes.data);
          // id 가 없을 때(응답 미포함 등) 이름으로 회원의 소속 브랜드를 매칭해 기본 선택
          const brandName = userData?.home_brand_name;
          if (userData && !userData.is_staff && userData.home_brand_id == null && brandName) {
            const matched = brandsRes.data.find((b) => b.name === brandName);
            if (matched) setSelectedBrandId(matched.id);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;

        // 상세가 이미 성공했다면 이 예외는 후속 단계(levels·brands 처리)에서 온 것이다.
        // 부가 정보가 없을 뿐 회원은 유효하므로 화면을 그대로 둔다.
        if (detailLoaded) return;

        // **UUID 상세가 404 여도 추측하지 않는다 (fail-closed).**
        //
        // 한때 여기서 같은 문자열로 목록을 검색해 `user_id` 정확 일치 행의 `public_id` 로
        // 이동했다 — UUID 모양 `user_id` 를 싣는 구 생산자를 구제하기 위해서였다. 그러나
        // 그 값이 `public_id` 가 아니라면 그것은 **로그인 아이디**이고, 검색은 그 아이디를
        // `GET ?search=` 로 내보내 access log·프록시에 원문으로 남긴다. 식별자를 URL 에서
        // 걷어낸 변경을 이 경로 하나가 되돌린다.
        //
        // 게다가 모양만으로는 확정할 수도 없다: 같은 문자열이 회원 A 의 `user_id` 이면서
        // 회원 B 의 `public_id` 일 수 있어, 검색 결과로 이동하면 **다른 회원의 상세**가 열리고
        // mutation 대상까지 바뀐다. `decideLegacyIdentifierDeepLink` 는 같은 이유로 이미
        // `ambiguous-blocked` 로 끊었다 — path route 도 같은 계약을 따른다.
        //
        // legacy `user_id` 는 navigate state(`MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID`)로만
        // 지원한다. 종류를 아는 생산자만 그 키를 실을 수 있고, URL 에는 남지 않는다.
        const status = (err as { response?: { status?: number } })?.response?.status;

        // 404 만 "찾을 수 없음" 이다. 네트워크·500 은 회원이 없다는 뜻이 아니므로
        // 같은 자리에 다른 문구로 알린다 — 관리자가 원인을 오해하지 않게.
        if (status === 404) {
          setNotFound(true);
        } else {
          setLoadError(getApiErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  // 강사 회원이면 강사 공개 상세 프로필을 별도 조회한다.
  // 관리자 토큰이므로 비공개(is_public=false) 강사도 200 으로 조회된다.
  // 프로필 미존재(404) 등 실패 시 강사 프로필 섹션을 숨긴다.
  useEffect(() => {
    if (!user?.is_staff || !user.user_id) {
      setInstructor(null);
      return;
    }
    let cancelled = false;
    // `cancelled` 만으로는 부족하다. 의존성이 `user` 에서 나오므로 라우트 `id` 가 A→B 로 바뀌어도
    // **B 상세가 도착하기 전까지** `user` 는 여전히 A 다 — 의존성이 그대로라 cleanup 이 돌지 않고,
    // 그 창에 도착한 A 의 강사 프로필이 B 화면에 그대로 적용된다. 의존성에 `id` 를 넣으면 그 창에서
    // 오히려 A 의 프로필을 **다시 요청**하게 되므로, 요청 시점의 `id` 를 떠 두고 적용 직전에 본다.
    const requestedGeneration = generationRef.current;
    usersService
      .getInstructorDetail(user.user_id)
      .then((res) => {
        if (cancelled || !stillViewing(requestedGeneration)) return;
        setInstructor(res.data);
        // 소속 브랜드 select 디폴트는 강사 프로필의 brand(InstructorProfile.brand)를 소스로 한다.
        setSelectedBrandId(res.data.brand ?? null);
      })
      .catch(() => {
        // 실패도 같은 세대에서만 반영한다 — A 의 프로필 조회 실패가 B 화면에 이미 뜬 프로필을
        // 지우면 안 된다.
        if (!cancelled && stillViewing(requestedGeneration)) setInstructor(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.is_staff, user?.user_id]);

  const roleLabel = () => {
    if (!user) return null;
    if (user.is_admin) return <Badge variant="rejected">관리자</Badge>;
    if (user.is_staff) return <Badge variant="info">강사</Badge>;
    return <Badge variant="default">수강생</Badge>;
  };

  const handleToggleActive = async () => {
    if (!user) return;
    // `id` 는 라우트 파라미터라 타입상 `string | undefined` 다. non-null 단언 대신
    // 여기서 한 번 좁혀 이후 요청·가드가 같은 값을 쓴다.
    const requestedId = id;
    if (!requestedId) return;
    const requestedGeneration = generationRef.current;
    const action = user.is_active ? "탈퇴" : "활성화";
    if (!window.confirm(`이 사용자를 ${action}하시겠습니까?`)) return;

    setSaving(true);
    try {
      const res = await usersService.adminManage(requestedId, { is_active: !user.is_active });
      if (!stillViewing(requestedGeneration)) return;
      setUser(res.data);
    } catch {
      alert(`사용자 ${action}에 실패했습니다.`);
    } finally {
      // **보고 있는 회원이 그대로일 때만** 잠금을 푼다. A 의 mutation 이 진행 중일 때
      // B 로 이동하면, 늦게 끝난 A 의 finally 가 B 화면의 `saving` 을 건드려 상태가
      // 어긋난다 (B 를 잠그거나, B 의 진행 중 잠금을 풀어 이중 제출을 연다).
      if (stillViewing(requestedGeneration)) setSaving(false);
    }
  };

  const handleRoleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const requestedId = id;
    if (!requestedId) return;
    const requestedGeneration = generationRef.current;
    if (!user) return;
    const newRole = e.target.value as RoleType;
    const label = ROLE_OPTIONS.find((r) => r.value === newRole)?.label ?? newRole;
    if (!window.confirm(`이 사용자의 역할을 "${label}"(으)로 변경하시겠습니까?`)) return;

    setSaving(true);
    try {
      const res = await usersService.adminManage(requestedId, roleToFlags(newRole));
      if (!stillViewing(requestedGeneration)) return;
      setUser(res.data);
    } catch {
      alert("역할 변경에 실패했습니다.");
    } finally {
      // **보고 있는 회원이 그대로일 때만** 잠금을 푼다. A 의 mutation 이 진행 중일 때
      // B 로 이동하면, 늦게 끝난 A 의 finally 가 B 화면의 `saving` 을 건드려 상태가
      // 어긋난다 (B 를 잠그거나, B 의 진행 중 잠금을 풀어 이중 제출을 연다).
      if (stillViewing(requestedGeneration)) setSaving(false);
    }
  };

  // 강사 소속 브랜드(InstructorProfile.brand) 변경 — 관리자 전용 단독 API 호출.
  const handleBrandChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const requestedId = id;
    if (!requestedId) return;
    const requestedGeneration = generationRef.current;
    if (!user) return;
    const value = e.target.value;
    const newBrandId = value === "" ? null : Number(value);
    const prevBrandId = selectedBrandId;
    const prevInstructor = instructor;

    const matchedName = brands.find((b) => b.id === newBrandId)?.name ?? null;
    const brandLabel = newBrandId == null ? "없음" : (matchedName ?? `#${newBrandId}`);
    if (!window.confirm(`이 강사의 소속 브랜드를 "${brandLabel}"(으)로 변경하시겠습니까?`)) return;

    // 낙관적 반영 — select + 강사 프로필 섹션 동시 갱신
    setSelectedBrandId(newBrandId);
    setInstructor((prev) =>
      prev
        ? { ...prev, brand: newBrandId, brand_name: newBrandId == null ? null : matchedName }
        : prev,
    );
    setSaving(true);
    try {
      const res = await usersService.updateInstructorBrand(requestedId, { brand_id: newBrandId });
      if (!stillViewing(requestedGeneration)) return;
      // 서버 확정값으로 정합화
      setSelectedBrandId(res.data.brand_id ?? null);
      setInstructor((prev) =>
        prev
          ? { ...prev, brand: res.data.brand_id ?? null, brand_name: res.data.brand_name ?? null }
          : prev,
      );
    } catch {
      // 실패 롤백에도 성공 경로와 **같은 세대 가드**가 필요하다. `prevInstructor` 는 A 의 강사
      // 프로필 객체라, A 의 PATCH 가 B 로 옮긴 뒤 실패하면 B 화면에 A 의 소개·자격증·소속
      // 브랜드가 복원된다. 이 화면에는 강사 프로필을 다시 읽어올 경로가 없어 **영구 잔류**하고,
      // 관리자가 그 값을 근거로 B 를 고치면 데이터가 오염된다. alert 도 같은 가드 안이다 —
      // A 의 실패를 B 화면에서 알릴 이유가 없다.
      if (!stillViewing(requestedGeneration)) return;
      alert("소속 브랜드 변경에 실패했습니다.");
      setSelectedBrandId(prevBrandId); // 실패 시 롤백
      setInstructor(prevInstructor);
    } finally {
      // **보고 있는 회원이 그대로일 때만** 잠금을 푼다. A 의 mutation 이 진행 중일 때
      // B 로 이동하면, 늦게 끝난 A 의 finally 가 B 화면의 `saving` 을 건드려 상태가
      // 어긋난다 (B 를 잠그거나, B 의 진행 중 잠금을 풀어 이중 제출을 연다).
      if (stillViewing(requestedGeneration)) setSaving(false);
    }
  };

  const handleLevelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const requestedId = id;
    if (!requestedId) return;
    const requestedGeneration = generationRef.current;
    if (!user) return;
    const value = e.target.value;
    const newLevel = value === "" ? null : Number(value);

    setSaving(true);
    try {
      const res = await usersService.adminManage(requestedId, { level: newLevel });
      if (!stillViewing(requestedGeneration)) return;
      setUser(res.data);
    } catch {
      alert("레벨 변경에 실패했습니다.");
    } finally {
      // **보고 있는 회원이 그대로일 때만** 잠금을 푼다. A 의 mutation 이 진행 중일 때
      // B 로 이동하면, 늦게 끝난 A 의 finally 가 B 화면의 `saving` 을 건드려 상태가
      // 어긋난다 (B 를 잠그거나, B 의 진행 중 잠금을 풀어 이중 제출을 연다).
      if (stillViewing(requestedGeneration)) setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ padding: 60, textAlign: "center", color: "#71717A" }}>불러오는 중...</div>
      </div>
    );
  }

  if (notFound || loadError || !user) {
    return (
      <div className="admin-page">
        <div style={{ padding: 60, textAlign: "center" }}>
          <p
            style={{ color: loadError ? "#EF4444" : "#71717A", marginBottom: 16 }}
            role={loadError ? "alert" : undefined}
          >
            {loadError
              ? `회원 정보를 불러오지 못했습니다 — ${loadError}`
              : "회원 정보를 찾을 수 없습니다."}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            뒤로가기
          </Button>
        </div>
      </div>
    );
  }

  // 소속 브랜드 옵션: 활성 브랜드 목록 + 현재 소속 브랜드(활성 목록에 없어도 노출).
  // 비활성 브랜드는 백엔드(BrandListView)에서 이미 제외되므로 목록에 포함되지 않는다.
  // 현재 소속 브랜드는 강사 프로필(instructor.brand)을 우선 소스로 하고, 미조회 시 회원의 home_brand 로 폴백.
  const brandOptions: { id: number; name: string }[] = brands.map((b) => ({
    id: b.id,
    name: b.name,
  }));
  const currentBrandId = instructor?.brand ?? user.home_brand_id;
  const currentBrandName = instructor ? instructor.brand_name : user.home_brand_name;
  if (currentBrandId != null && !brandOptions.some((b) => b.id === currentBrandId)) {
    brandOptions.unshift({
      id: currentBrandId,
      name: currentBrandName ?? `브랜드 #${currentBrandId}`,
    });
  }

  // 이 렌더가 만들어진 시점의 세대. 연락처 저장은 **모달 안**에서 발행되므로 부모는 요청
  // 시점을 알 수 없다 — 대신 요청을 실어 보낸 렌더의 콜백 **클로저에 세대를 고정**한다.
  // 응답 시점에 가변 ref 를 읽으면 안 된다: A 저장이 진행 중인 동안 B 로 이동해 모달을
  // 다시 열면 그 ref 가 B 세대로 덮여 가드가 `x === x` 로 퇴화하고, 뒤늦은 A 응답이
  // `setUser(A)` 로 B 화면을 오염시키며 낡은 `onClose` 가 B 의 모달까지 닫는다.
  // 세대가 오르는 지점(`id` 변경 effect)은 언제나 리렌더를 동반하므로, 렌더 시점의 값은
  // 그 렌더에서 떠나는 요청의 시점 값과 정확히 같다.
  const renderedGeneration = generationRef.current;

  return (
    <div className="admin-page">
      <ContentHeader
        title="회원 상세 정보"
        description={`${user.user_id} 회원의 상세 정보를 조회합니다.`}
        actions={[
          <Button key="back" variant="outline" size="sm" onClick={() => navigate(-1)}>
            ← 뒤로가기
          </Button>,
        ]}
      />

      <div className="admin-page__card user-detail">
        {/* 프로필 헤더 */}
        <div className="user-detail__header">
          <div className="user-detail__avatar">
            {user.profile_image_url ? (
              <img src={user.profile_image_url} alt={user.name} />
            ) : (
              <span>{user.name?.[0] ?? "?"}</span>
            )}
          </div>
          <div className="user-detail__header-info">
            <div className="user-detail__name">
              {user.name}
              {user.nickname && <span className="user-detail__nickname">({user.nickname})</span>}
            </div>
            <div className="user-detail__meta">
              <span className="user-detail__userid">{user.user_id}</span>
              {roleLabel()}
              {!user.is_active && <Badge variant="rejected">비활성</Badge>}
            </div>
            {user.bio && <p className="user-detail__bio">{user.bio}</p>}
          </div>
        </div>

        {/* 기본 정보 */}
        <div className="admin-page__section">
          <div className="user-detail__section-head">
            <div className="admin-page__section-title">기본 정보</div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setContactModalOpen(true)}
              disabled={saving}
            >
              회원정보 수정
            </Button>
          </div>
          <div className="user-detail__grid">
            <InfoRow label="이름" value={user.name} />
            <InfoRow label="아이디" value={`${user.user_id}`} />
            <InfoRow label="이메일" value={user.user_email ?? "—"} />
            <InfoRow label="휴대폰" value={user.hp ?? "—"} />
            <InfoRow label="선호 스포츠" value={user.preferred_sport_name ?? "—"} />
            <InfoRow label="브랜드" value={user.home_brand_name ?? "—"} />
            <InfoRow
              label="역할"
              value={user.is_admin ? "관리자" : user.is_staff ? "강사" : "수강생"}
            />
            <InfoRow label="상태" value={user.is_active ? "활성" : "비활성"} />
          </div>
        </div>

        {/* 강사 프로필 (강사 회원 + 프로필 존재 시) */}
        {user.is_staff && instructor && (
          <div className="admin-page__section">
            <div className="user-detail__section-head">
              <div className="admin-page__section-title">강사 프로필</div>
              <Badge variant={instructor.is_public ? "active" : "default"}>
                {instructor.is_public ? "공개" : "비공개"}
              </Badge>
            </div>

            <div className="user-detail__grid">
              <InfoRow label="소개" value={instructor.introduction || "—"} />
              <InfoRow label="관심사" value={instructor.interests || "—"} />
              <InfoRow label="소속 브랜드" value={instructor.brand_name ?? "—"} />
              <div className="user-detail__instructor-block">
                <span className="user-detail__info-label">자격증</span>
                {instructor.certificates.length > 0 ? (
                  <div className="user-detail__chips">
                    {instructor.certificates.map((c) => (
                      <span key={c.id} className="user-detail__tag">
                        {c.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="user-detail__info-value">—</span>
                )}
              </div>
              <div className="user-detail__instructor-block">
                <span className="user-detail__info-label">종목</span>
                {instructor.sports.length > 0 ? (
                  <div className="user-detail__chips">
                    {instructor.sports.map((s) => (
                      <span key={s.id} className="user-detail__tag">
                        {s.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="user-detail__info-value">—</span>
                )}
              </div>

              <div className="user-detail__instructor-block">
                <span className="user-detail__info-label">활동지역</span>
                {instructor.locations.length > 0 ? (
                  <div className="user-detail__chips">
                    {instructor.locations.map((l) => (
                      <span key={l.id} className="user-detail__tag">
                        {l.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="user-detail__info-value">—</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 태그 */}
        {user.tags && user.tags.length > 0 && (
          <div className="admin-page__section">
            <div className="admin-page__section-title">태그</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {user.tags.map((tag, i) => (
                <span key={i} className="user-detail__tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 배송지 */}
        {user.addresses && user.addresses.length > 0 && (
          <div className="admin-page__section">
            <div className="admin-page__section-title">배송지</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {user.addresses.map((addr) => (
                <div key={addr.id} className="user-detail__address">
                  <div className="user-detail__address-label">
                    {addr.label}
                    {addr.is_primary && <Badge variant="active">기본</Badge>}
                  </div>
                  <div className="user-detail__address-text">
                    [{addr.zipcode}] {addr.road_address} {addr.detail_address}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 사용자 관리 */}
        <div className="admin-page__section">
          <div className="admin-page__section-title">사용자 관리</div>
          <div className="user-detail__manage">
            <div className="user-detail__manage-row">
              <div className="user-detail__manage-label">
                <span className="user-detail__manage-title">계정 상태</span>
                <span className="user-detail__manage-desc">
                  사용자 계정을 강제 탈퇴/복구합니다.
                </span>
              </div>
              <Button
                variant={user.is_active ? "outline" : "primary"}
                size="sm"
                onClick={handleToggleActive}
                disabled={saving}
              >
                {user.is_active ? "강제 탈퇴" : "계정 복구"}
              </Button>
            </div>

            <div className="user-detail__manage-row">
              <div className="user-detail__manage-label">
                <span className="user-detail__manage-title">역할</span>
                <span className="user-detail__manage-desc">
                  수강생, 강사, 관리자 중 선택합니다.
                </span>
              </div>
              <select
                className="user-detail__select"
                value={getUserRole(user)}
                onChange={handleRoleChange}
                disabled={saving}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {user.is_staff && (
              <div className="user-detail__manage-row">
                <div className="user-detail__manage-label">
                  <span className="user-detail__manage-title">소속 브랜드</span>
                  <span className="user-detail__manage-desc">강사의 소속 브랜드를 변경합니다.</span>
                </div>
                <select
                  className="user-detail__select"
                  value={selectedBrandId ?? ""}
                  onChange={handleBrandChange}
                  disabled={saving}
                >
                  <option value="">-</option>
                  {brandOptions.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {levels.length > 0 && (
              <div className="user-detail__manage-row">
                <div className="user-detail__manage-label">
                  <span className="user-detail__manage-title">레벨</span>
                  <span className="user-detail__manage-desc">사용자의 레벨을 변경합니다.</span>
                </div>
                <select
                  className="user-detail__select"
                  value={user.level ?? ""}
                  onChange={handleLevelChange}
                  disabled={saving}
                >
                  <option value="">없음</option>
                  {levels.map((lv) => (
                    <option key={lv.id} value={lv.id}>
                      {lv.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <UserContactEditModal
        open={contactModalOpen}
        userId={id ?? ""}
        initialEmail={user.user_email}
        initialHp={user.hp}
        onClose={() => {
          // 낡은 세대의 저장이 뒤늦게 부르는 `onClose` 는 지금 열려 있는 **다른 회원의**
          // 모달을 닫는다. 현재 세대의 닫기(취소·오버레이·저장 완료)만 받는다.
          if (stillViewing(renderedGeneration)) setContactModalOpen(false);
        }}
        onSaved={(updated) => {
          // 모달이 저장을 마치는 사이 다른 회원으로 옮겨갔으면 반영하지 않는다.
          if (stillViewing(renderedGeneration)) setUser(updated);
        }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────
// 헬퍼
// ─────────────────────────────────────────────
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="user-detail__info-row">
    <span className="user-detail__info-label">{label}</span>
    <span className="user-detail__info-value">{value}</span>
  </div>
);

export default UserDetail;
